import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Gruppe } from '../../classes/gruppe';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from '../../../../services/message-service/message.service';
import { GruppenService } from '../../gruppen.service';
import { EinstallenService } from '../einstallen.service';
import { EinstallenMeldenDialogComponent } from '../einstallen-melden-dialog/einstallen-melden-dialog.component';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { EinstallenMeldenDialogInput, EinstallenMeldenDialogResult } from '../../classes/einstallen-melden';
import { AppService } from '../../../../services/app/app.service';

@Component({
    selector: 'kiene-einstallen-tiere-melden',
    templateUrl: './einstallen-tiere-melden.component.html',
    styleUrls: ['./einstallen-tiere-melden.component.scss'],
})
export class EinstallenTiereMeldenComponent implements OnInit, OnDestroy {
    tierSubscription: Subscription;

    currentGruppe: Gruppe;
    eingestallteTiere: GruppeTier[] = [];
    gefilterteTiere: GruppeTier[] = [];

    filterParams: HttpParams;
    suchfeldCtrl = new FormControl('');

    constructor(
        private readonly einstallService: EinstallenService,
        private readonly dialog: MatDialog,
        private readonly messageService: MessageService,
        private readonly gruppenService: GruppenService,
        private readonly appService: AppService,
    ) {}

    ngOnDestroy(): void {
        this.tierSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.currentGruppe = this.einstallService.getCurrentGruppe();
        if (this.currentGruppe) {
            this.loadTiere();
        }

        this.einstallService.watchCurrentGruppe().subscribe({
            next: val => {
                this.currentGruppe = val;
                this.loadTiere();
            },
        });

        this.einstallService.watchEingestallteTiere().subscribe({ next: () => this.loadTiere() });

        // Suchfeld-Änderungen überwachen
        this.suchfeldCtrl.valueChanges.subscribe({
            next: suchtext => {
                this.filtereTiereNachSuchtext(suchtext);
            },
        });
    }

    private loadTiere(orderBy = 'erstellt desc') {
        this.appService.setBlockOnLoading(true);
        this.eingestallteTiere = [];
        let params = new HttpParams().append('orderby', orderBy);
        if (this.filterParams) {
            this.filterParams.keys().forEach(key => {
                params = params.append(key, this.filterParams.get(key));
            });
        }
        this.tierSubscription?.unsubscribe();
        this.tierSubscription = this.gruppenService.ladeGruppenTiere(this.currentGruppe?.gruppe_id, params).subscribe({
            next: resp => {
                this.eingestallteTiere = resp.records;
                this.gefilterteTiere = [...this.eingestallteTiere];
                this.appService.setBlockOnLoading(false);
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Tiere!', err);
                this.appService.setBlockOnLoading(false);
            },
        });
    }

    melden() {
        const input = new EinstallenMeldenDialogInput();
        input.gruppe_id = this.currentGruppe?.gruppe_id;

        const dialogRef = this.dialog.open(EinstallenMeldenDialogComponent, {
            width: '100%',
            maxWidth: '400px',
            data: input,
        });
        dialogRef.afterClosed().subscribe({
            next: (result: EinstallenMeldenDialogResult) => {
                if (result?.ok) {
                    this.loadTiere();
                } else if (result?.tiereOhneEinstalldatumAnzeigen) {
                    this.filtern('einstalldatum_notnull', 'null');
                }
            },
        });
    }

    filtern(filterName: string, filterValue: string) {
        if (filterName && filterValue) {
            if (!this.filterParams) {
                this.filterParams = new HttpParams();
            }
            if (this.filterParams.has(filterName)) {
                this.filterParams = this.filterParams.delete(filterName);
                this.loadTiere();
            } else {
                this.filterParams = this.filterParams.set(filterName, filterValue);
                this.loadTiere();
            }
        }
    }

    filterZuruecksetzen() {
        this.filterParams = undefined;
        this.loadTiere();
    }

    isFilterActive(filterName: string) {
        if (this.filterParams && this.filterParams.has(filterName)) {
            return true;
        }
        return false;
    }

    filtereTiereNachSuchtext(suchtext: string) {
        if (!suchtext || suchtext.trim() === '') {
            this.gefilterteTiere = [...this.eingestallteTiere];
        } else {
            const suchtextLower = suchtext.toLowerCase().trim();
            this.gefilterteTiere = this.eingestallteTiere.filter(tier => {
                // Suche in Ohrmarke
                if (tier.ohrmarke && tier.ohrmarke.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                // Suche in Ohrmarke kurz
                if (tier.ohrmarke_kurz && tier.ohrmarke_kurz.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                // Suche in Ohrmarke 15-stellig
                if (tier.ohrmarke15 && tier.ohrmarke15.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                // Suche in Transpondernummer
                if (tier.transpondernummer && tier.transpondernummer.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                // Suche in Viehhändler
                if (tier.viehhaendler && tier.viehhaendler.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                // Suche in Abteil
                if (tier.abteil && tier.abteil.toLowerCase().includes(suchtextLower)) {
                    return true;
                }
                return false;
            });
        }
    }
}
